import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo, type JobCategory } from '../model/metaInfo';
import type {
  UploadMetaDataRequest,
  UploadMetaDataResponse,
  ReadMetaDataRequest,
  ReadMetaDataResponse,
} from '../model/types';

/**
 * Generic opaque MetaData body store (S3-backed on the service side).
 *
 * Content is an opaque string keyed by objectKey — this store knows nothing
 * about the document shape. Pure TS — no React. Calls {@link ApiClient} only.
 */

const PATHS = {
  upload: '/store/metadata/upload',
  read: '/store/metadata/read',
} as const;

export class MetaDataStore {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
    private readonly jobCategory: JobCategory = 'DOCUMENT_MANAGEMENT',
  ) {}

  async uploadMetaData(
    objectKey: string,
    content: string,
    contentType?: string,
  ): Promise<UploadMetaDataResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'UploadMetaData',
      jobCategory: this.jobCategory,
      requestType: 'UPLOAD_META_DATA',
    });
    return this.apiClient.post<UploadMetaDataRequest, UploadMetaDataResponse>(
      PATHS.upload,
      { metaInfo, objectKey, content, ...(contentType ? { contentType } : {}) },
    );
  }

  async readMetaData(objectKey: string): Promise<ReadMetaDataResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ReadMetaData',
      jobCategory: this.jobCategory,
      requestType: 'READ_META_DATA',
    });
    return this.apiClient.post<ReadMetaDataRequest, ReadMetaDataResponse>(
      PATHS.read,
      { metaInfo, objectKey },
    );
  }
}
